import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import { segurosApi } from '../../../lib/api';

export function PolizaFormDialog({ poliza, onClose, onSaved }) {
  const [form, setForm] = useState({
    aseguradora: poliza?.aseguradora || '',
    corredora: poliza?.corredora || '',
    numero_poliza: poliza?.numero_poliza || '',
    bien_asegurado: poliza?.bien_asegurado || '',
    uf_por_cuota: poliza?.uf_por_cuota ?? '',
    num_cuotas: poliza?.num_cuotas ?? '',
    dia_vencimiento: poliza?.dia_vencimiento ?? '',
    primer_vencimiento: poliza?.primer_vencimiento || '',
    notas: poliza?.notas || '',
    patente: poliza?.patente || '',
    vigencia: poliza?.vigencia || '',
    beneficiario: poliza?.beneficiario || '',
    responsable_pago: poliza?.responsable_pago || '',
    rut_beneficiario: poliza?.rut_beneficiario || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        uf_por_cuota: Number(form.uf_por_cuota),
        num_cuotas: Number(form.num_cuotas),
        dia_vencimiento: Number(form.dia_vencimiento),
      };
      if (poliza?.id) {
        await segurosApi.editarPoliza(poliza.id, payload);
        toast.success('✓ Póliza actualizada');
      } else {
        await segurosApi.crearPoliza(payload);
        toast.success('✓ Póliza creada con su cronograma de cuotas');
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="dialog-poliza-form">
        <DialogHeader>
          <DialogTitle className="text-base">{poliza ? 'Editar Póliza' : 'Nueva Póliza de Seguro'}</DialogTitle>
          <DialogDescription className="text-xs">
            La prima se expresa en UF. El sistema genera el cronograma de cuotas y calcula el valor en pesos con la UF del día (redondeo a 0 decimales).
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Aseguradora *</Label>
            <Input value={form.aseguradora} onChange={set('aseguradora')} className="h-8 text-sm" data-testid="input-aseguradora" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Corredora de Seguros</Label>
            <Input value={form.corredora} onChange={set('corredora')} className="h-8 text-sm" data-testid="input-corredora" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">N° Póliza *</Label>
            <Input value={form.numero_poliza} onChange={set('numero_poliza')} className="h-8 text-sm font-mono" data-testid="input-numero-poliza" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Bien asegurado *</Label>
            <Input value={form.bien_asegurado} onChange={set('bien_asegurado')} className="h-8 text-sm" data-testid="input-bien-asegurado" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Cantidad de UF por cuota *</Label>
            <Input type="number" step="0.0001" value={form.uf_por_cuota} onChange={set('uf_por_cuota')} className="h-8 text-sm" data-testid="input-uf-cuota" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Cantidad de cuotas *</Label>
            <Input type="number" min="1" value={form.num_cuotas} onChange={set('num_cuotas')} className="h-8 text-sm" data-testid="input-num-cuotas" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Día del mes de vencimiento *</Label>
            <Input type="number" min="1" max="31" value={form.dia_vencimiento} onChange={set('dia_vencimiento')} className="h-8 text-sm" data-testid="input-dia-vencimiento" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Primer vencimiento (mes) *</Label>
            <Input type="month" value={form.primer_vencimiento} onChange={set('primer_vencimiento')} className="h-8 text-sm" data-testid="input-primer-vencimiento" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Patente (si aplica)</Label>
            <Input value={form.patente} onChange={set('patente')} className="h-8 text-sm font-mono" data-testid="input-patente" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Fin de vigencia</Label>
            <Input type="date" value={form.vigencia} onChange={set('vigencia')} className="h-8 text-sm" data-testid="input-vigencia" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Beneficiario</Label>
            <Input value={form.beneficiario} onChange={set('beneficiario')} className="h-8 text-sm" data-testid="input-beneficiario" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">RUT Beneficiario</Label>
            <Input value={form.rut_beneficiario} onChange={set('rut_beneficiario')} className="h-8 text-sm font-mono" data-testid="input-rut-beneficiario" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Responsable de pago</Label>
            <Input value={form.responsable_pago} onChange={set('responsable_pago')} className="h-8 text-sm" data-testid="input-responsable-pago" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notas</Label>
            <Input value={form.notas} onChange={set('notas')} className="h-8 text-sm" data-testid="input-notas-poliza" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving} data-testid="btn-cancelar-poliza">Cancelar</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="btn-guardar-poliza">
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
